import { polotnoKey } from '@/environment';
import { useTiming } from '@/hooks/useTiming';
import { useSaveProjectArtifacts } from '@/pages/AdvancedEditor/hooks';
import { polotnoTypes, SIDEBAR_PANELS } from '@/pages/AdvancedEditor/types';
import { checkPolotnoType } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import { MixPanelEvent } from '@/utils/types';
import { IPresenter, IProjectTemplate } from '@d-id/types';
import { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import { ElementType, GroupElementType } from 'polotno/model/group-model';
import { createStore, StoreType } from 'polotno/model/store';
import { createContext, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCreatePagesThumbnails } from '../components/frames/useCreatePagesThumbnails';
import { useCurrentProjectId } from '../hooks/useCurrentProjectId';

const store = createStore({
    key: polotnoKey,
    showCredit: false,
});

export interface AdvancedEditorContextType {
    store: StoreType;
    hash: number;
    editorInitialized: boolean;
    setEditorInitialized: React.Dispatch<React.SetStateAction<boolean>>;

    // sidebar
    sidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
    activePanel: SIDEBAR_PANELS;
    setActivePanel: (panel: SIDEBAR_PANELS) => void;

    // delete presenter
    presenterToDelete: IPresenter | undefined;
    setPresenterToDelete: (presenter: IPresenter | undefined) => void;

    // save project artifacts
    debouncedSaveArtifacts: DebouncedFunc<(store: StoreType) => Promise<void>>;
    saveArtifacts: (store: StoreType, shouldSkipHashCheck?: boolean) => Promise<void>;

    // selected template
    selectedTemplate: IProjectTemplate | null;
    setSelectedTemplate: (template: IProjectTemplate | null) => void;
    selectedTemplateIndex: number | null;
    setSelectedTemplateIndex: (index: number | null) => void;

    // other
    createThumbnails: (store: StoreType) => Promise<void>;
    isGenerating: MutableRefObject<boolean>;
    scenesPageActionsFreeze: MutableRefObject<boolean>;
}

export const AdvancedEditorContext = createContext<AdvancedEditorContextType>({
    store,
    hash: 0,
    editorInitialized: false,
    setEditorInitialized: () => {},

    // sidebar
    sidebarOpen: true,
    setSidebarOpen: () => {},
    activePanel: SIDEBAR_PANELS.AVATARS,
    setActivePanel: () => {},

    // delete presenter
    presenterToDelete: undefined,
    setPresenterToDelete: () => {},

    // save project artifacts
    debouncedSaveArtifacts: debounce((_store: StoreType) => {}),
    saveArtifacts: async () => {},

    // selected template
    selectedTemplate: null,
    setSelectedTemplate: () => {},
    selectedTemplateIndex: null,
    setSelectedTemplateIndex: () => {},

    // other
    createThumbnails: async () => {},
    isGenerating: { current: false },
    scenesPageActionsFreeze: { current: false },
});

export const AdvancedEditorContextProvider = observer(({ children }: { children: ReactNode }) => {
    const mixpanel = useAnalytics();
    const { trace } = useTiming();
    const { state } = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activePanel, setActivePanel] = useState(state?.initPanel || SIDEBAR_PANELS.AVATARS);
    const [editorInitialized, setEditorInitialized] = useState(false);
    const [presenterToDelete, setPresenterToDelete] = useState<IPresenter | undefined>();
    const projectId = useCurrentProjectId();
    const { debouncedSaveArtifacts, saveArtifacts, hash } = useSaveProjectArtifacts(projectId);
    const { createThumbnails } = useCreatePagesThumbnails();
    const isGenerating = useRef(false);
    const scenesPageActionsFreeze = useRef(false);
    const [selectedTemplate, setSelectedTemplate] = useState<IProjectTemplate | null>(state?.selectedTemplate || null);
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | null>(state?.selectedTemplateIndex ?? null);

    useEffect(() => {
        store.clear();

        return () => {
            store.set({ custom: {} });
        };
    }, []);

    useEffect(() => {
        if (state?.isNewProject && editorInitialized) {
            mixpanel?.track('create-video', { event: MixPanelEvent.SUCCESS, from: state?.mixPanelFrom, videoId: projectId });
            trace('AdvancedEditorContext', { message: 'editor initialized', videoId: projectId });
        }
    }, [state, editorInitialized, projectId, mixpanel, trace]);

    useEffect(() => {
        const selectedElement = store.selectedElements[0];
        if (checkPolotnoType(selectedElement, polotnoTypes.Text)) {
            setActivePanel(SIDEBAR_PANELS.TEXT);
        }
        if (
            checkPolotnoType(selectedElement, polotnoTypes.Avatar) ||
            (checkPolotnoType(selectedElement, polotnoTypes.Group) &&
                (selectedElement as GroupElementType).children.some((child: ElementType) => checkPolotnoType(child, polotnoTypes.Avatar)))
        ) {
            setActivePanel(SIDEBAR_PANELS.AVATARS);
        }
        if (checkPolotnoType(selectedElement, [polotnoTypes.Video, polotnoTypes.Image, polotnoTypes.Svg])) {
            setActivePanel(SIDEBAR_PANELS.MEDIA);
        }
        if (checkPolotnoType(selectedElement, [polotnoTypes.Figure, polotnoTypes.Line])) {
            setActivePanel(SIDEBAR_PANELS.SHAPES);
        }
    }, [JSON.stringify(store.selectedElements)]);

    return (
        <AdvancedEditorContext.Provider
            value={{
                hash,
                store,
                editorInitialized,
                setEditorInitialized,
                sidebarOpen,
                setSidebarOpen,
                activePanel,
                setActivePanel,
                presenterToDelete,
                setPresenterToDelete,
                debouncedSaveArtifacts,
                saveArtifacts,
                createThumbnails,
                isGenerating,
                scenesPageActionsFreeze,
                selectedTemplate,
                setSelectedTemplate,
                selectedTemplateIndex,
                setSelectedTemplateIndex,
            }}>
            {children}
        </AdvancedEditorContext.Provider>
    );
});
